<template>
	<div class="w-full mx-auto px-4 pb-4 space-y-5">
		<Card>
		<div class="w-full bg-background">
			<div class="border-b border-muted">
				<div class="flex space-x-8 relative pt-2 px-5">
					<button class="relative pb-3 text-subtitle after:content-[''] after:absolute after:-bottom-px after:left-0 after:h-0.5 after:w-full" :class="{'after:bg-primary text-primary' : uploadTab == 'media'}" @click.prevent="uploadTab='media'">
						Media
					</button>
					<button class="relative pb-3 text-subtitle after:content-[''] after:absolute after:-bottom-px after:left-0 after:h-0.5 after:w-full" :class="{'after:bg-primary text-primary' : uploadTab == 'text'}" @click.prevent="uploadTab='text'">
						Articles
					</button>
				</div>
			</div>
		</div>

		<CardContent class="space-y-4 bg-background p-4" v-if="uploadTab=='media'">
		<div class="flex justify-between space-x-4">
			<div class="basis-2/3 flex flex-col space-y-3">
				<FormField label="Title" name="title">
				<Input v-model="media.title" placeholder="Title" />
				</FormField>

				<FormField label="Description" name="description">
				<Textarea v-model="media.message" placeholder="Description" rows="4" />
				</FormField>

				<FormField label="Group" name="group">
				<Select v-model="media.groupid">
					<SelectTrigger>
					<SelectValue placeholder="Select a group" />
					</SelectTrigger>
				<SelectContent>
				<SelectItem v-for="group in groups" :key="group.id" :value="group.id">
				{{ group.name }}
				</SelectItem>
				</SelectContent>
				</Select>
				</FormField>

				<FormField label="Channel" name="channel">
				<Select v-model="media.channel">
					<SelectTrigger>
					<SelectValue placeholder="Select channel" />
					</SelectTrigger>
				<SelectContent>
				<SelectItem v-for="channel in channels" :key="channel.id" :value="channel.id">
				{{ channel.name }}
				</SelectItem>
				</SelectContent>
				</Select>
				</FormField>

				<FormField v-if="collections.length > 0" label="Collection" name="collection">
				<Select v-model="media.collectionid">
					<SelectTrigger>
					<SelectValue placeholder="Select collection (optional)" />
					</SelectTrigger>
				<SelectContent>
				<SelectItem value="">None</SelectItem>
				<SelectItem v-for="col in collections" :key="col.id" :value="col.id">
				{{ col.name }}
				</SelectItem>
				</SelectContent>
				</Select>
				</FormField>

				<FormField label="Tags" name="tags">
				<Input v-model="media.tags" placeholder="Comma-separated tags" />
				</FormField>
			</div>
			<div class="basis-1/3 flex flex-col space-y-3">
				<UploadBox v-model="logo" size="full" @file-selected="(f: File) => media.file = f" />
			</div>
		</div>

		<!-- Metadata Pre-population -->
		<div v-if="metaFields.length > 0" class="border rounded-md">
			<button @click="metaExpanded = !metaExpanded" class="flex items-center gap-2 w-full p-3 text-sm font-medium hover:bg-muted/50 transition-colors">
				<component :is="metaExpanded ? ChevronDownIcon : ChevronRightIcon" class="h-4 w-4"/>
				Metadata
				<span class="text-xs text-muted-foreground font-normal ml-1">({{ metaFields.length }} fields{{ selectedKeywords.length > 0 ? ', ' + selectedKeywords.length + ' keywords' : '' }})</span>
			</button>
			<div v-if="metaExpanded" class="px-4 pb-4 space-y-4">
				<div v-for="[groupName, fields] in groupedMeta" :key="groupName">
					<p class="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-2">{{ groupName }}</p>
					<div class="grid grid-cols-1 md:grid-cols-2 gap-3">
						<div v-for="field in fields" :key="field.id" class="space-y-1">
							<Label class="text-xs">
								{{ field.label }}
								<span v-if="field.required" class="text-destructive">*</span>
							</Label>
							<Select v-if="field.type === 'dropdown' && field.parsedOptions.length > 0" v-model="metaValues[field.label]">
								<SelectTrigger class="h-8 text-xs">
									<SelectValue :placeholder="'Select ' + field.label" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem v-for="opt in field.parsedOptions" :key="opt" :value="opt">{{ opt }}</SelectItem>
								</SelectContent>
							</Select>
							<Select v-else-if="field.type === 'multiselect' && field.parsedOptions.length > 0" v-model="metaValues[field.label]">
								<SelectTrigger class="h-8 text-xs">
									<SelectValue :placeholder="'Select ' + field.label" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem v-for="opt in field.parsedOptions" :key="opt" :value="opt">{{ opt }}</SelectItem>
								</SelectContent>
							</Select>
							<Textarea v-else-if="field.type === 'textarea'" v-model="metaValues[field.label]" rows="2" class="text-xs" :placeholder="field.label" />
							<div v-else-if="field.type === 'checkbox'" class="flex items-center gap-2 pt-1">
								<input type="checkbox" :checked="metaValues[field.label] === 'true'" @change="metaValues[field.label] = ($event.target as HTMLInputElement).checked ? 'true' : 'false'" class="rounded border-input" />
								<span class="text-xs">{{ field.label }}</span>
							</div>
							<Input v-else-if="field.type === 'datefield' || field.type === 'date'" type="date" v-model="metaValues[field.label]" class="h-8 text-xs" />
							<Input v-else-if="field.type === 'numeric'" type="number" v-model="metaValues[field.label]" class="h-8 text-xs" :placeholder="field.label" />
							<Input v-else v-model="metaValues[field.label]" class="h-8 text-xs" :placeholder="field.label" />
						</div>
					</div>
				</div>

				<!-- Keyword Taxonomy Search -->
				<div class="border-t pt-4">
					<p class="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-2">
						<Tag class="h-3.5 w-3.5 inline mr-1" />Keywords
					</p>

					<!-- Selected keywords pills -->
					<div v-if="selectedKeywords.length > 0" class="flex flex-wrap gap-1.5 mb-3">
						<Badge v-for="kw in selectedKeywords" :key="kw.id" variant="secondary" class="text-xs gap-1 pr-1">
							{{ kw.label }}
							<button class="ml-0.5 hover:text-destructive" @click="removeKeyword(kw)">
								<X class="h-3 w-3" />
							</button>
						</Badge>
					</div>

					<!-- Search input -->
					<div class="relative">
						<Search class="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
						<Input v-model="keywordSearch" placeholder="Search keywords (min 4 chars)..." class="pl-8 h-8 text-xs" @input="onKeywordInput" />
					</div>

					<!-- Search results -->
					<div v-if="keywordSearching" class="text-xs text-muted-foreground mt-2">Searching...</div>
					<div v-else-if="keywordResults.length > 0" class="border rounded-md mt-2 max-h-40 overflow-y-auto">
						<button
							v-for="kw in keywordResults"
							:key="kw.id"
							class="w-full text-left px-3 py-1.5 text-xs hover:bg-muted transition-colors flex items-center gap-2"
							:class="{ 'bg-primary/10 text-primary': selectedKeywords.some(s => s.id === kw.id) }"
							@click="toggleKeyword(kw)"
						>
							<span v-if="selectedKeywords.some(s => s.id === kw.id)" class="text-primary">✓</span>
							{{ kw.label }}
							<span class="text-muted-foreground ml-auto">#{{ kw.id }}</span>
						</button>
					</div>
					<div v-else-if="keywordSearch.length >= 4 && !keywordSearching" class="text-xs text-muted-foreground mt-2">No results found</div>
				</div>
			</div>
		</div>

		<div class="flex items-center gap-3">
			<Button :disabled="uploading" @click="uploadMedia">
				{{ uploading ? 'Uploading...' : 'Upload Media' }}
			</Button>
			<p v-if="uploadStatus" class="text-sm" :class="uploadStatus.includes('failed') ? 'text-destructive' : 'text-green-600'">{{ uploadStatus }}</p>
		</div>
		</CardContent>

		<!-- ARTICLE TAB -->
		<CardContent class="space-y-4 bg-background p-4" v-if="uploadTab=='text'">
                <div class="flex justify-between space-x-4">
                        <div class="basis-2/3 flex flex-col space-y-3">
				<FormField label="Title" name="title">
				<Input v-model="article.title" placeholder="Enter article title..." />
				</FormField>

				<FormField label="HTML Body" name="hTML Body">
				<QuillEditor v-model:content="article.body" contentType="html" theme="snow" style="height:450px"/>
				</FormField>
			</div>
			<div class="basis-1/3 flex flex-col space-y-3">

				<FormField label="Group" name="group">
				<Select v-model="media.groupid">
					<SelectTrigger>
					<SelectValue placeholder="Select a group" />
					</SelectTrigger>
				<SelectContent>
				<SelectItem v-for="group in groups" :key="group.id" :value="group.id">
				{{ group.name }}
				</SelectItem>
				</SelectContent>
				</Select>
				</FormField>
				<FormField label="Channel" name="channel">
				<Select v-model="article.channel">
					<SelectTrigger>
					<SelectValue placeholder="Select channel" />
					</SelectTrigger>
					<SelectContent>
					<SelectItem v-for="channel in channels" :key="channel.id" :value="channel.id">
					{{ channel.name }}
					</SelectItem>
					</SelectContent>
				</Select>
				</FormField>

				<div class="grid grid-cols-2 gap-4">
					<FormField label="Latitude" name="latitude">
					<Input v-model="article.geo_latitude" placeholder="Latitude 47.32"/>
					</FormField>
					<FormField label="Longitude" name="longitude">
					<Input v-model="article.geo_longitude" placeholder="Longitude 87.32"/>
					</FormField>
				</div>

				<div class="space-y-1">
				<Label class="text-sm">Email (optional)</Label>
				<Input v-model="article.userData.email" placeholder="Contributor email" />
			</div>

			<div class="space-y-1">
				<Label class="text-sm">First Name (optional)</Label>
				<Input v-model="article.userData.firstname" placeholder="Contributor name" />
			</div>
			</div>
		</div>

		<div class="flex items-center gap-3">
			<Button :disabled="uploading" @click="uploadArticle">
				{{ uploading ? 'Uploading...' : 'Upload Article' }}
			</Button>
			<p v-if="uploadStatus" class="text-sm" :class="uploadStatus.includes('failed') ? 'text-destructive' : 'text-green-600'">{{ uploadStatus }}</p>
		</div>
		</CardContent>
		</Card>
	</div>

</template>

<script setup lang="ts">
import { Ref, inject, ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import FormField from '@/components/ui/form-field.vue'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { QuillEditor } from '@vueup/vue-quill'
import UploadBox from '@/components/UploadBox.vue'
import { ChevronDown as ChevronDownIcon, ChevronRight as ChevronRightIcon, Search, X, Tag } from 'lucide-vue-next'
import { Badge } from '@/components/ui/badge'
import { useGroups } from '@/services/Groups'
import { useChannels } from '@/services/Channels'
import vhostSvc from '@/services/Vhost'

import '@vueup/vue-quill/dist/vue-quill.snow.css'
const vhostId = inject<Ref<string>>('vhostId', ref(""));
const { getGroups: gqlGetGroups } = useGroups()
const { getChannels: gqlGetChannels, channelList: gqlChannelList } = useChannels()
const { getCustomMeta: gqlGetCustomMeta } = vhostSvc()
const uploading = ref(false)
const logo = ref('')
const uploadStatus = ref('')
const uploadTab = ref('media')

interface MetaField {
	id: number
	label: string
	name: string
	type: string
	grouping: string
	provider: string
	required: number
	options: string
	defaultValue: string
	sort: number
	source: string
	parsedOptions: string[]
}

// ─── Keyword taxonomy search state ───
interface KeywordItem {
	id: number
	label: string
	parentid?: number
	externalid?: string
	children?: KeywordItem[]
}
const keywordSearch = ref('')
const keywordResults = ref<KeywordItem[]>([])
const keywordSearching = ref(false)
const selectedKeywords = ref<KeywordItem[]>([])
let keywordDebounce: ReturnType<typeof setTimeout> | null = null

const media = ref({
	title: '',
	message: '',
	file: null as File | null,
	groupid: '',
	channel: '',
	collectionid: '',
	tags: '',
})

const article = ref({
	title: '',
	body: '',
	channel: '',
	geo_latitude: '',
	geo_longitude: '',
	userData: {
		email: '',
		firstname: '',
	},
	recaptchaToken: '',
})

const groups = ref<{ id: string; name: string }[]>([])
const channels = ref<{ id: string; name: string }[]>([])
const collections = ref<{ id: string; name: string }[]>([])
const metaFields = ref<MetaField[]>([])
const metaValues = ref<Record<string, string>>({})
const metaExpanded = ref(true)

const groupedMeta = computed(() => {
	const map = new Map<string, MetaField[]>()
	for (const f of metaFields.value) {
		const group = f.grouping || 'General'
		if (!map.has(group)) map.set(group, [])
		map.get(group)!.push(f)
	}
	return map
})

async function fetchGroups() {
	try {
		const result = await gqlGetGroups(Number(vhostId.value))
		groups.value = result.map((g: any) => ({ id: String(g.id), name: g.name }))
	} catch (err) {
		console.error('Error fetching groups:', err)
	}
}

async function fetchChannels() {
	try {
		await gqlGetChannels(vhostId.value)
		channels.value = gqlChannelList.value.map((c: any) => ({ id: String(c.id), name: c.name }))
	} catch (err) {
		console.error('Error fetching channels:', err)
	}
}

async function fetchMetaFields() {
	try {
		const result = await gqlGetCustomMeta(Number(vhostId.value))
		metaFields.value = (result || [])
			.filter((f: any) => !f.source || f.source === 'manual')
			.filter((f: any) => f.provider !== 'survey' && f.provider !== 'quiz')
			.map((f: any) => {
			// Parse options — GQL returns as JSON string, legacy returns as array
			let parsedOptions: string[] = []
			if (f.options) {
				if (Array.isArray(f.options)) {
					parsedOptions = f.options
				} else if (typeof f.options === 'string') {
					try {
						const parsed = JSON.parse(f.options)
						parsedOptions = Array.isArray(parsed) ? parsed : [String(parsed)]
					} catch {
						parsedOptions = f.options.includes('|')
							? f.options.split('|').filter(Boolean)
							: f.options.split(',').map((s: string) => s.trim()).filter(Boolean)
					}
				}
			}
			return {
				...f,
				provider: f.provider || '',
				parsedOptions,
			}
		})
		// Pre-fill defaults using label as key (matching legacy uploader pattern)
		for (const f of metaFields.value) {
			if (f.defaultValue) {
				metaValues.value[f.label] = f.defaultValue
			}
		}
	} catch (err) {
		console.error('Error fetching metadata fields:', err)
	}
}

// ─── Keyword taxonomy search ───
function onKeywordInput() {
	if (keywordDebounce) clearTimeout(keywordDebounce)
	const q = keywordSearch.value.trim()
	if (q.length < 4) {
		keywordResults.value = []
		return
	}
	keywordDebounce = setTimeout(() => searchKeywords(q), 400)
}

async function searchKeywords(query: string) {
	keywordSearching.value = true
	try {
		const res = await axios.post('/services/json', {
			method: 'vhost.get3rdPartyMetaData',
			vhostID: vhostId.value,
			query,
		})
		if (res.data?.status && res.data.result) {
			// Flatten the tree to a simple list for selection
			const flat: KeywordItem[] = []
			function flatten(items: KeywordItem[]) {
				for (const item of items) {
					if (item.id > 0) flat.push({ id: item.id, label: item.label, externalid: item.externalid })
					if (item.children?.length) flatten(item.children)
				}
			}
			flatten(res.data.result)
			keywordResults.value = flat
		} else {
			keywordResults.value = []
		}
	} catch {
		keywordResults.value = []
	} finally {
		keywordSearching.value = false
	}
}

function toggleKeyword(kw: KeywordItem) {
	const idx = selectedKeywords.value.findIndex((k) => k.id === kw.id)
	if (idx >= 0) {
		selectedKeywords.value.splice(idx, 1)
	} else {
		selectedKeywords.value.push(kw)
	}
}

function removeKeyword(kw: KeywordItem) {
	selectedKeywords.value = selectedKeywords.value.filter((k) => k.id !== kw.id)
}

function handleFileUpload(event: Event) {
	const target = event.target as HTMLInputElement
	if (target.files?.length) media.value.file = target.files[0]
}

async function uploadMedia() {
	if (!media.value.file) return

	uploading.value = true
	uploadStatus.value = ''

	const file = media.value.file
	// Build metadata using label as key (matching legacy uploader: meta[metaItem.label])
	const userMeta: Record<string, string> = {}
	for (const [label, val] of Object.entries(metaValues.value)) {
		if (val && val.trim() !== '') userMeta[label] = val
	}
	// Add selected keywords with their IDs
	for (const kw of selectedKeywords.value) {
		userMeta[`keyword:${kw.id}`] = kw.label
	}

	const fileData: Record<string, any> = {
		filename: file.name,
		title: media.value.title,
		message: media.value.message,
		type: file.type,
		groupid: media.value.groupid,
	}
	if (media.value.channel) fileData.channel = media.value.channel
	if (media.value.collectionid) fileData.collectionid = media.value.collectionid
	if (media.value.tags) fileData.tags = media.value.tags
	// Send metadata wrapped in { user: ... } matching legacy format
	if (Object.keys(userMeta).length > 0) {
		fileData.metadata = { user: userMeta }
	}

	const body = {
		method: 'media.getUploadUrl',
		vhost: vhostId.value,
		v2: 1,
		fileData,
	}

	try {
		const res = await fetch('/services/json', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		})
		const json = await res.json()

		if (json.status) {
			const url = json.result.url
			const mediaID = json.result.id

			const uploadRes = await fetch(url, {
				method: 'PUT',
				headers: { 'Content-Type': file.type },
				body: file,
			})

			if (!uploadRes.ok) throw new Error('Upload failed')

			await fetch('/services/json', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ method: 'media.uploadCompleted', id: mediaID }),
			})

			uploadStatus.value = 'Media uploaded and finalized!'
		} else {
			throw new Error('getUploadUrl failed')
		}
	} catch (err) {
		console.error(err)
		uploadStatus.value = 'Upload failed.'
	} finally {
		uploading.value = false
	}
}

async function uploadArticle() {
	if (!article.value.title || !article.value.body) {
		uploadStatus.value = 'Please enter both title and body.'
		return
	}

	uploading.value = true
	uploadStatus.value = ''

	try {
		const fileData: any = {
			title: article.value.title,
			body: article.value.body,
			contenttype: 'text/html',
			extension: 'html',
		}

		if (article.value.channel) fileData.channel = article.value.channel
		if (article.value.geo_latitude) fileData.geo_latitude = article.value.geo_latitude
		if (article.value.geo_longitude) fileData.geo_longitude = article.value.geo_longitude
		if (article.value.userData?.email) fileData.userData = article.value.userData

		const body: any = {
			method: 'media.insertTextFile',
			vhost: vhostId.value,
			fileData,
		}

		if (article.value.recaptchaToken) {
			body.recaptchaToken = article.value.recaptchaToken
		}

		const res = await fetch('/services/json', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		})

		const json = await res.json()
		if (json?.status !== 1) throw new Error('Insert failed.')

		uploadStatus.value = 'Article submitted successfully!'
	} catch (err) {
		console.error(err)
		uploadStatus.value = 'Article submission failed.'
	} finally {
		uploading.value = false
	}
}

onMounted(() => {
	fetchGroups()
	fetchChannels()
	fetchMetaFields()
})
</script>

<style scoped>
.quill {
	min-height: 200px;
}
</style>
